import { useMemo, useState } from 'react';
import { LinkIcon } from 'lucide-react';

import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import { ENVIRONMENTS_POLLING_INTERVAL } from '@/react/portainer/environments/queries/useEnvironmentList';
import { AttachmentsDataTable } from '@/react/portainer/policies/DetailView/AttachmentsDataTables/AttachmentsDataTable';
import { AppliedPolicyStatusType } from '@/react/portainer/environments/types';
import { PolicyStatusBreakdown } from '@/react/portainer/policies/types';

import { Alert } from '@@/Alert';
import { Widget } from '@@/Widget';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';

import { usePolicy } from '../../queries/usePolicy';
import { useUpdatePolicyMutation } from '../../queries/useUpdatePolicyMutation';

import { PolicyStatusBar } from './PolicyStatusBar';
import { getStatusDisplayLabel } from './statusUtils';
import { EnvironmentGroupsSelector } from './EnvironmentGroupsSelector';

interface Props {
  policyId: number;
}

export function AttachmentsDataTables({ policyId }: Props) {
  const policyQuery = usePolicy(policyId, {
    refetchInterval: ENVIRONMENTS_POLLING_INTERVAL,
  });
  const groupsQuery = useGroups({ size: true });
  const [statusFilter, setStatusFilter] = useState<
    AppliedPolicyStatusType | undefined
  >(undefined);
  const [search, setSearch] = useState('');

  const updateMutation = useUpdatePolicyMutation();

  const policy = policyQuery.data;
  const groups = groupsQuery.data;

  const policyGroups = useMemo(
    () =>
      groups?.filter(
        (group) => policy?.EnvironmentGroups?.includes(group.Id)
      ) || [],
    [groups, policy]
  );

  const isLoading = policyQuery.isLoading || groupsQuery.isLoading;
  const isError = !!policyQuery.error || !!groupsQuery.error;

  // Check if filter would return no results
  const noFilterResults =
    statusFilter &&
    !isLoading &&
    getFilteredCount(statusFilter, policy?.StatusBreakdown) === 0;

  if (policyQuery.error || groupsQuery.error) {
    return (
      <div className="row">
        <div className="col-sm-12">
          <Alert color="error" title="Error loading policy or groups">
            {(policyQuery.error as Error)?.message ??
              (groupsQuery.error as Error)?.message ??
              'Unknown error'}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="row" data-cy="policy-attachments-container">
      <div className="col-sm-12">
        <Widget>
          <Widget.Title icon={LinkIcon} title="Attachments" />
          <div className="flex flex-col gap-4 px-5 pt-4 pb-4">
            <EnvironmentGroupsSelector
              policy={policy}
              groups={groups}
              updateMutation={updateMutation}
            />
            <div className="flex items-center">
              {policyGroups.length === 0 ? (
                <TextTip color="orange" className="small">
                  No environment groups attached to this policy
                </TextTip>
              ) : (
                <div className="small">
                  Displaying environments across all groups
                </div>
              )}
            </div>
          </div>
          <Widget.Body>
            {isError ? (
              <Alert color="error" title="Error loading policy or groups">
                {(policyQuery.error as Error)?.message ??
                  (groupsQuery.error as Error)?.message ??
                  'Unknown error'}
              </Alert>
            ) : (
              <>
                <PolicyStatusBar
                  statusBreakdown={policy?.StatusBreakdown}
                  statusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                  isLoading={isLoading}
                  search={search}
                  onSearchChange={setSearch}
                />
                {noFilterResults && !!statusFilter ? (
                  <NoFilterResultsMessage
                    statusFilter={statusFilter}
                    onClearFilter={() => setStatusFilter(undefined)}
                  />
                ) : (
                  <div className="flex flex-col gap-4">
                    {policyGroups.map((group) => (
                      <AttachmentsDataTable
                        key={group.Id}
                        group={group}
                        policy={policy}
                        isPolicyLoading={isLoading}
                        statusFilter={statusFilter}
                        search={search}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </Widget.Body>
        </Widget>
      </div>
    </div>
  );
}

/**
 * Get the count of environments matching a status filter from the breakdown.
 * @param status - The status type to filter by
 * @param breakdown - The policy status breakdown counts
 * @returns The count of environments with the given status
 */
export function getFilteredCount(
  status: AppliedPolicyStatusType,
  breakdown?: PolicyStatusBreakdown
): number {
  if (!breakdown) return 0;
  switch (status) {
    case AppliedPolicyStatusType.Applied:
      return breakdown.Applied;
    case AppliedPolicyStatusType.Warning:
      return breakdown.Warning;
    case AppliedPolicyStatusType.Failed:
      return breakdown.Failed;
    case AppliedPolicyStatusType.InProgress:
      return breakdown.InProgress;
    case AppliedPolicyStatusType.NotSupported:
      return breakdown.NotSupported;
    default:
      return 0;
  }
}

interface NoFilterResultsMessageProps {
  statusFilter: AppliedPolicyStatusType;
  onClearFilter(): void;
}

function NoFilterResultsMessage({
  statusFilter,
  onClearFilter,
}: NoFilterResultsMessageProps) {
  const statusLabel = getStatusDisplayLabel(statusFilter);
  return (
    <div
      className="flex flex-col items-center justify-center py-8 text-center"
      data-cy="no-filter-results"
    >
      <p className="text-muted mb-2">
        No environment groups with &quot;{statusLabel}&quot; status
      </p>
      <Button
        color="link"
        onClick={onClearFilter}
        data-cy="clear-status-filter"
      >
        Clear filter
      </Button>
    </div>
  );
}
